import React, { FC, FocusEventHandler, useState } from 'react';
import Select, { MultiValue, SingleValue, components } from 'react-select';
import { Chip, Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

import { Badge, Text } from '../../shared/uiComponents';
import { BACKGROUND, PRIMARY, RED, STROKE } from '../../shared/Helpers/colors';
import { InsuranceProperties } from '../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import { getBranchBadgeType } from '../../shared/Helpers/constants';

interface SelectOption {
	branches?: Array<{ id: string; name: string }>;
	isDisabled?: boolean;
	isSubItem?: boolean;
	label: string;
	value: string;
}

interface GroupOption {
	label: string;
	options: SelectOption[];
	originalItem: InsuranceProperties;
}

interface BaseProperties {
	className?: string;
	data: InsuranceProperties[] | null;
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	label?: string;
	onBlur?: FocusEventHandler<HTMLInputElement>;
}

interface SingleSelectProperties extends BaseProperties {
	isClearable?: boolean;
	setValue: (value: string | null) => void;
	value: string | null;
}

interface MultiSelectProperties extends BaseProperties {
	selectedBranchIds?: string[];
	setValue: (value: string[]) => void;
	userBranches?: Array<{ id: string; isAutoAssigned?: boolean; name: string }>;
	value: string[];
}

const shouldShowBranchChips = (insurance: InsuranceProperties): boolean => {
	if (!insurance.subInsurances || insurance.subInsurances.length === 0) {
		return false;
	}

	const parentBranchIds = insurance.branches?.map((b) => b.id) || [];

	return insurance.subInsurances.some((sub) => {
		const subBranchIds = sub.branches?.map((b) => b.id) || [];
		if (subBranchIds.length !== parentBranchIds.length) return true;

		return subBranchIds.some((id) => !parentBranchIds.includes(id));
	});
};

const buildOptionsFromData = (data: InsuranceProperties[] | null) => {
	if (!data) return { allOptions: [], groupedOptions: [], finalOptions: [] };

	const allOptions: SelectOption[] = [];
	const groupedOptions: GroupOption[] = [];
	const subInsuranceIds = new Set<string>();

	data.forEach((item) => {
		if (item.subInsurances && item.subInsurances.length > 0) {
			item.subInsurances.forEach((sub) => subInsuranceIds.add(sub.id));
		}
	});

	data.forEach((item) => {
		if (subInsuranceIds.has(item.id)) return;

		if (item.subInsurances && item.subInsurances.length > 0) {
			const subOptions: SelectOption[] = item.subInsurances.map((sub) => ({
				value: sub.id,
				label: sub.name,
				isSubItem: true,
				isDisabled: !sub.canBeAssigned,
				branches: sub.branches,
			}));

			allOptions.push(...subOptions);
			groupedOptions.push({
				label: item.name,
				options: subOptions,
				originalItem: item,
			});
		} else {
			allOptions.push({
				value: item.id,
				label: item.name,
				isDisabled: !item.canBeAssigned,
				branches: item.branches,
			});
		}
	});

	const finalOptions = [...allOptions.filter((opt) => !opt.isSubItem), ...groupedOptions];

	return { allOptions, groupedOptions, finalOptions };
};

const CustomOption = (props: any) => {
	const { data, innerProps, isFocused, isDisabled, isSelected } = props;

	return (
		<div
			{...innerProps}
			style={{
				padding: '8px 12px',
				paddingLeft: data.isSubItem ? '30px' : '12px',
				backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.08)' : isFocused ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
				color: isDisabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
				opacity: isDisabled ? 0.7 : 1,
				cursor: isDisabled ? 'not-allowed' : 'pointer',
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
				<Text title={data.label} />
				{data.branches && data.branches.length > 0 && (
					<>
						{data.branches.map((branch: any) => (
							<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
						))}
					</>
				)}
			</div>
		</div>
	);
};

const CustomGroup = (props: any) => {
	const { children, label, data } = props;
	const [open, setOpen] = useState(false);

	if (React.Children.count(children) === 0) return null;

	const isGroupDisabled = data.options.every((opt: SelectOption) => opt.isDisabled);
	const showChips = shouldShowBranchChips(data.originalItem);

	return (
		<div style={{ marginBottom: '0' }}>
			<div
				onClick={(e) => {
					e.stopPropagation();
					if (!isGroupDisabled) setOpen(!open);
				}}
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '8px 12px',
					cursor: isGroupDisabled ? 'not-allowed' : 'pointer',
					opacity: isGroupDisabled ? 0.7 : 1,
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<Text title={label} />
					{showChips && data.originalItem.branches && data.originalItem.branches.length > 0 && (
						<>
							{data.originalItem.branches.map((branch: any) => (
								<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
							))}
						</>
					)}
				</div>
				{open ? <ExpandLess /> : <ExpandMore />}
			</div>
			<Collapse in={open} timeout="auto" unmountOnExit>
				<div>{children}</div>
			</Collapse>
		</div>
	);
};

const CustomSingleValue = (props: any) => {
	const { data } = props;
	const shouldShowBranches = data.isSubItem || (data.branches && data.branches.length > 0);

	return (
		<components.SingleValue {...props}>
			<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
				<Text title={data.label} />
				{shouldShowBranches && data.branches && data.branches.length > 0 && (
					<>
						{data.branches.map((branch: any) => (
							<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
						))}
					</>
				)}
			</div>
		</components.SingleValue>
	);
};

const getSelectStyles = (error: boolean, disabled: boolean, isMulti: boolean) => ({
	control: (provided: any) => ({
		...provided,
		border: `1px solid ${error ? RED.BASE : STROKE.SECONDARY}`,
		borderRadius: '10px',
		background: BACKGROUND.PRIMARY[100],
		minHeight: '40px',
		height: isMulti ? 'auto' : '40px',
		boxShadow: 'none',
		cursor: disabled ? 'not-allowed' : 'pointer',
	}),
	valueContainer: (provided: any) => ({
		...provided,
		padding: isMulti ? '4px 8px' : 0,
		maxHeight: isMulti ? '100px' : undefined,
		overflowY: isMulti ? 'auto' : undefined,
		height: isMulti ? undefined : '100%',
		flexWrap: 'wrap',
	}),
	input: (provided: any, state: any) => ({
		...provided,
		padding: isMulti ? undefined : '8px 10px',
		margin: isMulti ? '2px' : 0,
		boxShadow: state.isFocused ? `0 0 0 0.2rem ${PRIMARY.BASE}` : 'none',
	}),
	singleValue: (provided: any) => ({
		...provided,
		padding: '8px 10px',
		margin: 0,
	}),
	placeholder: (provided: any) => ({
		...provided,
		padding: '8px 10px',
		color: STROKE.SECONDARY,
	}),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: (provided: any) => ({
		...provided,
		padding: '8px',
		color: STROKE.SECONDARY,
	}),
	menu: (provided: any) => ({
		...provided,
		zIndex: 9999,
		maxWidth: '450px',
		boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
		borderRadius: '4px',
		marginTop: '8px',
	}),
	menuList: (provided: any) => ({
		...provided,
		padding: '8px',
	}),
	option: (provided: any) => ({
		...provided,
		padding: '0',
		backgroundColor: 'transparent',
	}),
});

export const InsuranceSelect: FC<SingleSelectProperties> = ({
	data,
	value,
	setValue,
	label = '',
	description = '',
	error = false,
	disabled = false,
	errorMessage = '',
	className = '',
	onBlur,
	isClearable = false,
}) => {
	const { allOptions, finalOptions } = buildOptionsFromData(data);
	const selectedOption = allOptions.find((opt) => opt.value === value);

	return (
		<div className={className}>
			<div style={{ width: '100%' }}>
				<Text title={label} className={`inputLabel ${label ? 'active' : ''}`} type="psm" fontWeight="medium" />
				<Select<SelectOption>
					options={finalOptions}
					value={selectedOption}
					onChange={(option: SingleValue<SelectOption>) => setValue(option ? option.value : null)}
					isDisabled={disabled}
					onBlur={onBlur}
					styles={getSelectStyles(error, disabled, false)}
					components={{
						Option: CustomOption,
						Group: CustomGroup,
						SingleValue: CustomSingleValue,
						// TODO - Type properly using react-select typings
						DropdownIndicator: (props: any) => (
							<components.DropdownIndicator {...props}>
								{props.selectProps.menuIsOpen ? <ExpandLess /> : <ExpandMore />}
							</components.DropdownIndicator>
						),
					}}
					isClearable={isClearable}
				/>
				{error && (
					<Text className="error active" title={errorMessage} fontWeight="medium" type="psm" color={RED.BASE} />
				)}
				{!error && description && (
					<Text className="inputDescription active" title={description} fontWeight="medium" type="psm" />
				)}
			</div>
		</div>
	);
};

export const InsuranceMultiSelect: FC<MultiSelectProperties> = ({
	data,
	value,
	setValue,
	selectedBranchIds = [],
	userBranches = [],
	label = '',
	description = '',
	error = false,
	disabled = false,
	errorMessage = '',
	className = '',
	onBlur,
}) => {
	const filterBranchIds =
		selectedBranchIds.length > 0 ? selectedBranchIds : userBranches.filter((b) => b.isAutoAssigned).map((b) => b.id);

	const filteredData =
		data?.filter((insurance) => {
			if (filterBranchIds.length === 0) return true;
			if (!insurance.branches || insurance.branches.length === 0) return true;

			return insurance.branches.some((branch) => filterBranchIds.includes(branch.id));
		}) ?? null;

	const { allOptions, finalOptions } = buildOptionsFromData(filteredData);
	const selectedOptions = allOptions.filter((opt) => value.includes(opt.value));
	const visibleOptionIds = allOptions.map((opt) => opt.value);

	const CustomMultiValue = (props: any) => {
		const shouldShowBranches = props.data.isSubItem || (props.data.branches && props.data.branches.length > 0);

		return (
			<Chip
				label={
					<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
						<span>{props.data.label}</span>
						{shouldShowBranches && props.data.branches && props.data.branches.length > 0 && (
							<>
								{props.data.branches.map((branch: any) => (
									<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
								))}
							</>
						)}
					</div>
				}
				onDelete={() => props.removeProps.onClick()}
				size="small"
				sx={{
					margin: '2px',
					height: 'auto',
					'& .MuiChip-label': { padding: '4px 8px' },
				}}
			/>
		);
	};

	return (
		<div className={className}>
			<div style={{ width: '100%' }}>
				<Text title={label} className={`inputLabel ${label ? 'active' : ''}`} type="psm" fontWeight="medium" />
				<Select<SelectOption, true>
					isMulti
					options={finalOptions}
					value={selectedOptions}
					onChange={(options: MultiValue<SelectOption>) => {
						const hiddenSelectedIds = value.filter((id) => !visibleOptionIds.includes(id));
						setValue([...hiddenSelectedIds, ...options.map((opt) => opt.value)]);
					}}
					isDisabled={disabled}
					onBlur={onBlur}
					styles={getSelectStyles(error, disabled, true)}
					components={{
						Option: CustomOption,
						Group: CustomGroup,
						MultiValue: CustomMultiValue,
						// TODO - Type props properly using react-select typings
						DropdownIndicator: (props: any) => (
							<components.DropdownIndicator {...props}>
								{props.selectProps.menuIsOpen ? <ExpandLess /> : <ExpandMore />}
							</components.DropdownIndicator>
						),
					}}
					isClearable={false}
					closeMenuOnSelect={false}
					menuPlacement="top"
				/>
				{error && (
					<Text className="error active" title={errorMessage} fontWeight="medium" type="psm" color={RED.BASE} />
				)}
				{!error && description && (
					<Text className="inputDescription active" title={description} fontWeight="medium" type="psm" />
				)}
			</div>
		</div>
	);
};

export default InsuranceSelect;
